import React, { useState } from 'react'
import { useQuery } from 'react-query'
import { useTranslation } from 'react-i18next'
import { motion } from 'framer-motion'
import Card from '../../components/ui/Card'
import Button from '../../components/ui/Button'
import LoadingSpinner from '../../components/ui/LoadingSpinner'
import {
  ChartBarIcon,
  CalendarIcon,
  CurrencyDollarIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  DocumentArrowDownIcon
} from '@heroicons/react/24/outline'
import { budgetAPI, dashboardAPI } from '../../services/api'
import { ExpenseTrendChart, CategoryPieChart, ExpenseBarChart } from '../../components/charts'
import useTheme from '../../hooks/useTheme'
import useLanguage from '../../hooks/useLanguage'
import toast from 'react-hot-toast'

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.08 },
  },
}

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.4 } },
}

const ReportsPage = () => {
  const { t } = useTranslation('budget')
  const { isDark } = useTheme()
  const { locale } = useLanguage()
  const [selectedPeriod, setSelectedPeriod] = useState('monthly')
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear())
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1)

  const { data: expensesResponse, isLoading } = useQuery(
    ['expenses', selectedPeriod, selectedYear, selectedMonth],
    () => budgetAPI.getExpenses(),
    {
      onError: () => {
        toast.error(t('reports.fetchError'))
      }
    }
  )

  const expenses = expensesResponse?.data?.expenses || expensesResponse?.expenses || (Array.isArray(expensesResponse) ? expensesResponse : [])

  const { data: dashboardData } = useQuery(
    'dashboard',
    dashboardAPI.getDashboard,
  )

  const monthlyTrend = dashboardData?.data?.monthly_trend || []

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: 'PLN'
    }).format(amount)
  }

  const calculateStats = () => {
    if (!Array.isArray(expenses) || expenses.length === 0) return null

    const filteredExpenses = expenses.filter(expense => {
      const expenseDate = new Date(expense.expense_date || expense.date)
      const expenseMonth = expenseDate.getMonth() + 1
      const expenseYear = expenseDate.getFullYear()

      if (selectedPeriod === 'monthly') {
        return expenseMonth === selectedMonth && expenseYear === selectedYear
      } else if (selectedPeriod === 'yearly') {
        return expenseYear === selectedYear
      }
      return true
    })

    const totalAmount = filteredExpenses.reduce((sum, expense) => sum + parseFloat(expense.amount || 0), 0)
    const expenseCount = filteredExpenses.length
    const avgExpense = expenseCount > 0 ? totalAmount / expenseCount : 0

    const categoryStats = filteredExpenses.reduce((acc, expense) => {
      const categoryName = expense.category?.name || expense.Category?.name || t('reports.noCategory')
      if (!acc[categoryName]) {
        acc[categoryName] = { amount: 0, count: 0 }
      }
      acc[categoryName].amount += parseFloat(expense.amount || 0)
      acc[categoryName].count += 1
      return acc
    }, {})

    const sortedCategories = Object.entries(categoryStats)
      .map(([name, stats]) => ({ name, ...stats }))
      .sort((a, b) => b.amount - a.amount)

    const previousPeriodExpenses = expenses.filter(expense => {
      const expenseDate = new Date(expense.expense_date || expense.date)
      const expenseMonth = expenseDate.getMonth() + 1
      const expenseYear = expenseDate.getFullYear()

      if (selectedPeriod === 'monthly') {
        const prevMonth = selectedMonth === 1 ? 12 : selectedMonth - 1
        const prevYear = selectedMonth === 1 ? selectedYear - 1 : selectedYear
        return expenseMonth === prevMonth && expenseYear === prevYear
      } else if (selectedPeriod === 'yearly') {
        return expenseYear === selectedYear - 1
      }
      return false
    })

    const previousTotal = previousPeriodExpenses.reduce((sum, expense) => sum + parseFloat(expense.amount || 0), 0)
    const changePercent = previousTotal > 0 ? ((totalAmount - previousTotal) / previousTotal) * 100 : 0

    return {
      totalAmount,
      expenseCount,
      avgExpense,
      categoryStats: sortedCategories,
      changePercent,
      previousTotal
    }
  }

  const stats = calculateStats()

  const exportData = () => {
    if (!expenses) return

    const dataStr = JSON.stringify(expenses, null, 2)
    const dataUri = 'data:application/json;charset=utf-8,' + encodeURIComponent(dataStr)

    const exportFileDefaultName = `wydatki_${selectedPeriod}_${selectedYear}${selectedPeriod === 'monthly' ? `_${selectedMonth}` : ''}.json`

    const linkElement = document.createElement('a')
    linkElement.setAttribute('href', dataUri)
    linkElement.setAttribute('download', exportFileDefaultName)
    linkElement.click()

    toast.success(t('reports.exportSuccess'))
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <LoadingSpinner size="lg" text={t('reports.loading')} />
      </div>
    )
  }

  return (
    <motion.div
      className="space-y-6"
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      <motion.div variants={itemVariants} className="md:flex md:items-center md:justify-between">
        <div className="min-w-0 flex-1">
          <p className="text-sm text-[var(--color-text-muted)]">
            {t('reports.subtitle')}
          </p>
        </div>
        <div className="mt-4 flex md:ml-4 md:mt-0">
          <Button onClick={exportData} variant="outline">
            <DocumentArrowDownIcon className="h-4 w-4 mr-2" />
            {t('reports.export')}
          </Button>
        </div>
      </motion.div>

      {/* Filtry */}
      <motion.div variants={itemVariants}>
        <Card>
          <Card.Body>
            <div className="flex flex-wrap gap-4 items-end">
              <div>
                <label htmlFor="period" className="block text-sm font-medium text-[var(--color-text)]">
                  {t('reports.periodLabel')}
                </label>
                <select
                  id="period"
                  value={selectedPeriod}
                  onChange={(e) => setSelectedPeriod(e.target.value)}
                  className="form-select mt-1"
                >
                  <option value="monthly">{t('reports.periodMonthly')}</option>
                  <option value="yearly">{t('reports.periodYearly')}</option>
                </select>
              </div>

              <div>
                <label htmlFor="year" className="block text-sm font-medium text-[var(--color-text)]">
                  {t('reports.yearLabel')}
                </label>
                <select
                  id="year"
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                  className="form-select mt-1"
                >
                  {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i).map(year => (
                    <option key={year} value={year}>{year}</option>
                  ))}
                </select>
              </div>

              {selectedPeriod === 'monthly' && (
                <div>
                  <label htmlFor="month" className="block text-sm font-medium text-[var(--color-text)]">
                    {t('reports.monthLabel')}
                  </label>
                  <select
                    id="month"
                    value={selectedMonth}
                    onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
                    className="form-select mt-1"
                  >
                    {Array.from({ length: 12 }, (_, i) => i + 1).map(month => (
                      <option key={month} value={month}>
                        {new Date(2024, month - 1).toLocaleDateString(locale, { month: 'long' })}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>
          </Card.Body>
        </Card>
      </motion.div>

      {/* Statystyki główne */}
      {stats && (
        <motion.div variants={itemVariants} className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
          <Card>
            <Card.Body>
              <div className="flex items-center">
                <div className="shrink-0 rounded-lg bg-success-100 p-2.5">
                  <CurrencyDollarIcon className="h-6 w-6 text-success-600" />
                </div>
                <div className="ml-4 w-0 flex-1">
                  <p className="text-sm font-medium text-[var(--color-text-muted)] truncate">{t('reports.totalExpenses')}</p>
                  <p className="text-lg font-bold text-[var(--color-text)]">{formatCurrency(stats.totalAmount)}</p>
                </div>
              </div>
            </Card.Body>
          </Card>

          <Card>
            <Card.Body>
              <div className="flex items-center">
                <div className="shrink-0 rounded-lg bg-primary-100 p-2.5">
                  <ChartBarIcon className="h-6 w-6 text-primary-600" />
                </div>
                <div className="ml-4 w-0 flex-1">
                  <p className="text-sm font-medium text-[var(--color-text-muted)] truncate">{t('reports.expenseCount')}</p>
                  <p className="text-lg font-bold text-[var(--color-text)]">{stats.expenseCount}</p>
                </div>
              </div>
            </Card.Body>
          </Card>

          <Card>
            <Card.Body>
              <div className="flex items-center">
                <div className="shrink-0 rounded-lg bg-purple-100 p-2.5">
                  <CalendarIcon className="h-6 w-6 text-purple-600" />
                </div>
                <div className="ml-4 w-0 flex-1">
                  <p className="text-sm font-medium text-[var(--color-text-muted)] truncate">{t('reports.avgExpense')}</p>
                  <p className="text-lg font-bold text-[var(--color-text)]">{formatCurrency(stats.avgExpense)}</p>
                </div>
              </div>
            </Card.Body>
          </Card>

          <Card>
            <Card.Body>
              <div className="flex items-center">
                <div className={`shrink-0 rounded-lg p-2.5 ${stats.changePercent >= 0 ? 'bg-error-100' : 'bg-success-100'}`}>
                  {stats.changePercent >= 0 ? (
                    <ArrowTrendingUpIcon className="h-6 w-6 text-error-600" />
                  ) : (
                    <ArrowTrendingDownIcon className="h-6 w-6 text-success-600" />
                  )}
                </div>
                <div className="ml-4 w-0 flex-1">
                  <p className="text-sm font-medium text-[var(--color-text-muted)] truncate">{t('reports.changeVsPrevious')}</p>
                  <p className={`text-lg font-bold ${stats.changePercent >= 0 ? 'text-error-600' : 'text-success-600'}`}>
                    {stats.changePercent >= 0 ? '+' : ''}{stats.changePercent.toFixed(1)}%
                  </p>
                </div>
              </div>
            </Card.Body>
          </Card>
        </motion.div>
      )}

      {/* Wykresy */}
      {stats && stats.expenseCount > 0 && (
        <motion.div variants={itemVariants} className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          {/* Trend Chart */}
          {monthlyTrend.length > 0 && (
            <Card padding={false}>
              <Card.Header>
                <h3 className="text-lg font-semibold text-[var(--color-text)]">{t('reports.expenseTrend')}</h3>
                <p className="text-sm text-[var(--color-text-muted)]">{t('reports.monthlyComparison')}</p>
              </Card.Header>
              <Card.Body>
                <ExpenseTrendChart data={monthlyTrend} isDark={isDark} height={300} />
              </Card.Body>
            </Card>
          )}

          {/* Category Pie Chart */}
          {stats.categoryStats.length > 0 && (
            <Card padding={false}>
              <Card.Header>
                <h3 className="text-lg font-semibold text-[var(--color-text)]">{t('reports.categoryBreakdown')}</h3>
                <p className="text-sm text-[var(--color-text-muted)]">{t('reports.categoryShare')}</p>
              </Card.Header>
              <Card.Body>
                <CategoryPieChart
                  data={stats.categoryStats.map((c) => ({ name: c.name, total: c.amount }))}
                  isDark={isDark}
                  height={280}
                />
              </Card.Body>
            </Card>
          )}
        </motion.div>
      )}

      {/* Bar Chart - kategorie */}
      {stats && stats.categoryStats.length > 0 && (
        <motion.div variants={itemVariants}>
          <Card padding={false}>
            <Card.Header>
              <h3 className="text-lg font-semibold text-[var(--color-text)]">{t('reports.expensesByCategory')}</h3>
              <p className="text-sm text-[var(--color-text-muted)]">{t('reports.amountComparison')}</p>
            </Card.Header>
            <Card.Body>
              <ExpenseBarChart
                data={stats.categoryStats.map((c) => ({ name: c.name, amount: c.amount }))}
                isDark={isDark}
                height={320}
              />
            </Card.Body>
          </Card>
        </motion.div>
      )}

      {/* Brak danych */}
      {(!stats || stats.expenseCount === 0) && (
        <motion.div variants={itemVariants}>
          <Card>
            <Card.Body>
              <div className="text-center py-12">
                <ChartBarIcon className="mx-auto h-12 w-12 text-[var(--color-text-faint)]" />
                <h3 className="mt-2 text-sm font-medium text-[var(--color-text)]">{t('reports.noData')}</h3>
                <p className="mt-1 text-sm text-[var(--color-text-muted)]">
                  {t('reports.noExpensesInPeriod')}
                </p>
                <div className="mt-6">
                  <Button href="/dashboard/budget/expenses">
                    {t('reports.addFirst')}
                  </Button>
                </div>
              </div>
            </Card.Body>
          </Card>
        </motion.div>
      )}
    </motion.div>
  )
}

export default ReportsPage
